/**
 * Daily / Weekly / Monthly Offers — shared section rendered on both
 * Home and Store. Prices are ALWAYS charged in GBP server-side; this
 * component shows the customer's chosen display currency as the
 * headline number and clarifies "charged in GBP" below when they
 * aren't already on GBP.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useCurrency } from "../lib/currency";
import { useAuth } from "../lib/auth";
import { useStoreStatus } from "../lib/storeStatus";
import { Timer, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/** Human-readable countdown from now to an ISO timestamp. Backend is the
 *  source of truth — we tick locally only to animate the number. */
function useCountdown(iso) {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!iso) return;
    const tick = () => setRemaining(Math.max(0, Math.floor((new Date(iso).getTime() - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [iso]);
  const days = Math.floor(remaining / 86400);
  const hrs = Math.floor((remaining % 86400) / 3600);
  const mins = Math.floor((remaining % 3600) / 60);
  const secs = remaining % 60;
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  parts.push(`${String(hrs).padStart(2, "0")}h`, `${String(mins).padStart(2, "0")}m`, `${String(secs).padStart(2, "0")}s`);
  return parts.join(" ");
}

function OfferCard({ offer, onPurchase, purchasing, format, code, isBase, salesPaused }) {
  const countdown = useCountdown(offer.resets_at);
  const remaining = offer.remaining_in_period ?? offer.limit_per_period;
  const soldOut = remaining <= 0;
  const savingPct = Math.round((offer.saving_gbp / offer.normal_equivalent_price_gbp) * 100);
  const accent = {
    daily: "border-[var(--av-gold)] shadow-[0_0_25px_-10px_rgba(212,175,55,0.5)]",
    weekly: "border-[var(--av-purple)] shadow-[0_0_25px_-10px_rgba(148,0,211,0.5)]",
    monthly: "border-[var(--av-gold)] shadow-[0_0_30px_-8px_rgba(212,175,55,0.55)]",
  }[offer.id];
  return (
    <div data-testid={`offer-card-${offer.id}`}
         className={`vault-card p-6 relative overflow-hidden ring-2 ${accent}`}>
      <div className="absolute top-3 -right-8 rotate-45 w-32 text-center py-1 text-[10px] font-serif font-bold tracking-wider uppercase shadow-lg bg-gradient-to-r from-[#d4af37] to-[#a17c1a] text-[#0b0c10]">
        {`SAVE ${savingPct}%`}
      </div>
      <div className="mono-caption purple-text mb-2">{offer.name.toUpperCase()}</div>
      <div className="font-serif text-2xl font-bold gold-text mb-1">
        {offer.underlying_pack_quantity} × {offer.underlying_apex_amount.toLocaleString()} Apex Coin Packs
      </div>
      <div className="text-sm text-[var(--av-text-dim)] mb-3">
        {offer.total_apex.toLocaleString()} total Apex
      </div>
      <div className="flex items-baseline gap-2 mb-1">
        <span className="font-serif text-3xl font-bold gold-text" data-testid={`offer-price-${offer.id}`}>
          {format(offer.special_price_gbp)}
        </span>
        <span className="text-sm text-[var(--av-text-muted)] line-through decoration-[var(--av-purple)]/70">
          {format(offer.normal_equivalent_price_gbp)}
        </span>
      </div>
      <div className="text-[11px] purple-text font-semibold mb-1">
        You save {format(offer.saving_gbp)}
      </div>
      {!isBase && (
        <div className="text-[10px] text-[var(--av-text-muted)] mb-2" data-testid={`offer-gbp-note-${offer.id}`}>
          ≈ approximate in {code} · charged £{offer.special_price_gbp.toFixed(2)} GBP
        </div>
      )}
      <div className="text-xs text-[var(--av-text)] mb-3">
        <Sparkles className="w-3 h-3 text-[var(--av-purple)] inline align-[-2px] mr-1"/>
        Earn Apex Rewards on this offer. Rewards <span className="purple-text font-semibold">cannot</span> be redeemed against offers.
      </div>
      {/* Reset countdown + remaining */}
      <div className="rounded-lg border border-[var(--av-border)] bg-[rgba(11,12,16,0.5)] p-3 mb-3">
        <div className="flex justify-between items-center text-[11px] mono-caption text-[var(--av-text-dim)] mb-1">
          <span className="flex items-center gap-1"><Timer className="w-3 h-3"/> Resets in</span>
          <span data-testid={`offer-remaining-${offer.id}`}>
            {remaining} of {offer.limit_per_period} left
          </span>
        </div>
        <div data-testid={`offer-countdown-${offer.id}`}
             className="font-mono text-lg gold-text tabular-nums">
          {countdown}
        </div>
        <div className="text-[9px] text-[var(--av-text-muted)] mt-1">
          Limit: {offer.limit_per_period} per {offer.period} · resets at 00:00 UTC
        </div>
      </div>
      <button
        disabled={soldOut || purchasing}
        data-testid={`offer-buy-${offer.id}`}
        onClick={() => onPurchase(offer)}
        className="btn-gold w-full disabled:opacity-40 disabled:cursor-not-allowed">
        {soldOut ? "Allowance used — try after reset" : (purchasing ? "Processing…" : `Buy for ${format(offer.special_price_gbp)}`)}
      </button>
    </div>
  );
}

export default function SpecialOffersSection() {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const { format, code, isBase } = useCurrency();
  const { salesPaused } = useStoreStatus();
  const nav = useNavigate();
  const load = () => api.get("/special-offers").then(r => setData(r.data)).catch(() => setData({ offers: [] }));
  useEffect(() => {
    load();
    // Poll every 30s so a customer sitting on the page sees their
    // allowance update if they buy from another tab, and countdowns
    // stay reset-accurate.
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [user?.id]);
  const purchase = (offer) => {
    if (!user) {
      toast.error("Please sign in to buy an offer");
      nav(`/auth?mode=login&next=${encodeURIComponent(`/offer-checkout/${offer.id}`)}`);
      return;
    }
    nav(`/offer-checkout/${offer.id}`);
  };
  if (!data) return null;
  const offers = data.offers || [];
  if (offers.length === 0) return null;
  return (
    <section id="special-offers" className="scroll-mt-28" data-testid="special-offers-section">
      <div className="flex flex-wrap justify-between items-baseline gap-2 mb-3">
        <h2 className="font-serif text-3xl font-bold">
          <span className="gold-text">Daily, Weekly &amp; Monthly Offers</span>
        </h2>
        {!data.live_mode && (
          <div className="pill pill-purple text-[10px] flex items-center gap-1" data-testid="offers-test-mode-badge">
            <AlertTriangle className="w-3 h-3"/> TEST MODE — purchases are simulated
          </div>
        )}
      </div>
      <p className="text-[var(--av-text-dim)] mb-5 text-sm">
        Discounted Apex Coin bundles. Earn your normal Apex Rewards; existing rewards can't be redeemed against these offers.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {offers.map(o => (
          <OfferCard key={o.id} offer={o} onPurchase={purchase} purchasing={false}
                     format={format} code={code} isBase={isBase} salesPaused={salesPaused}/>
        ))}
      </div>
    </section>
  );
}
